import React, { useState } from 'react';
import {
  Car,
  MapPin,
  Fuel,
  Battery,
  Gauge,
  RefreshCw,
  Lock,
  Unlock,
  Lightbulb,
  Package,
  Navigation,
  X,
  Clock,
  DollarSign,
  Download,
  AlertTriangle
} from 'lucide-react';

const VehiclePage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'current'>('current');
  const [showEndTrip, setShowEndTrip] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [showTripInfo, setShowTripInfo] = useState(false);
  const [vehicleStatus, setVehicleStatus] = useState<'空闲' | '使用中'>('空闲');

  // 计算行程时长
  const calculateTripDuration = () => {
    const now = new Date().getTime();
    const duration = now - vehicleData.currentTrip.startTimestamp;
    const hours = Math.floor(duration / (1000 * 60 * 60));
    const minutes = Math.floor((duration % (1000 * 60 * 60)) / (1000 * 60));
    return { hours, minutes };
  };

  // 模拟车辆数据
  const vehicleData = {
    plateNumber: '粤A·12345',
    model: '岚图梦想家',
    status: '使用中',
    location: '深圳市福田区福景大厦',
    fuelLevel: 45.2, // 燃油剩余量(L)
    batteryLevel: 32.5, // 电量剩余量(KWH)
    mileage: 12580,
    lastUpdate: '2分钟前',
    currentTrip: {
      startTime: '2024-01-15 14:30',
      startMileage: 12560,
      startFuel: 52.8, // 起始燃油量(L)
      startBattery: 38.2, // 起始电量(KWH)
      currentMileage: 12580,
      currentFuel: 45.2, // 当前燃油量(L)
      currentBattery: 32.5, // 当前电量(KWH)
      startTimestamp: new Date('2024-01-15T14:30:00').getTime()
    }
  };

  // 消耗统计数据
  const consumptionStats = [
    {
      label: '已行驶里程',
      value: `${vehicleData.currentTrip.currentMileage - vehicleData.currentTrip.startMileage} km`,
      icon: Gauge
    },
    {
      label: '行程时长',
      value: '2时15分',
      icon: Clock
    },
    {
      label: '燃油消耗',
      value: `${(vehicleData.currentTrip.startFuel - vehicleData.currentTrip.currentFuel).toFixed(1)}L`,
      icon: Fuel
    },
    {
      label: '电量消耗',
      value: `${(vehicleData.currentTrip.startBattery - vehicleData.currentTrip.currentBattery).toFixed(1)}KWH`,
      icon: Battery
    }
  ];

  const renderVehicleInfoCard = () => (
    <div className="bg-white rounded-xl p-6 mb-6 shadow-sm">
      {/* 车辆基础信息 */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">{vehicleData.plateNumber}</h1>
          <p className="text-gray-600">{vehicleData.model}</p>
        </div>
        <div className="flex items-center space-x-2">
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${vehicleStatus === '空闲'
              ? 'bg-green-100 text-green-800'
              : 'bg-blue-100 text-blue-800'
            }`}>
            {vehicleStatus}
          </span>
          <button className="p-2 text-gray-400 hover:text-gray-600">
            <RefreshCw size={20} />
          </button>
        </div>
      </div>

      {/* 实时位置 */}
      <div className="mb-4">
        <div className="flex items-center space-x-2 mb-2">
          <MapPin size={20} className="text-blue-500" />
          <span className="text-sm text-gray-600">实时位置</span>
        </div>
        <div className="bg-gray-100 rounded-lg p-3 flex items-center justify-between">
          <span className="text-sm text-gray-700">{vehicleData.location}</span>
          <button className="text-blue-600 text-sm font-medium">导航</button>
        </div>
      </div>

      {/* 能耗信息 */}
      <div className="grid grid-cols-3 gap-4">
        <div className="text-center">
          <div className="flex items-center justify-center mb-2">
            <Fuel size={20} className="text-orange-500" />
          </div>
          <div className="text-lg font-bold text-gray-800">{vehicleData.fuelLevel}L</div>
          <div className="text-xs text-gray-500">燃油剩余</div>
        </div>
        <div className="text-center">
          <div className="flex items-center justify-center mb-2">
            <Battery size={20} className="text-green-500" />
          </div>
          <div className="text-lg font-bold text-gray-800">{vehicleData.batteryLevel}KWH</div>
          <div className="text-xs text-gray-500">电量剩余</div>
        </div>
        <div className="text-center">
          <div className="flex items-center justify-center mb-2">
            <Gauge size={20} className="text-blue-500" />
          </div>
          <div className="text-lg font-bold text-gray-800">{vehicleData.mileage}</div>
          <div className="text-xs text-gray-500">里程(km)</div>
        </div>
      </div>

      <div className="text-xs text-gray-400 text-center mt-3">
        数据更新时间：{vehicleData.lastUpdate}
      </div>
    </div>
  );

  const renderControlButtons = () => (
    <div className="bg-white rounded-xl p-4 mb-4 shadow-sm">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Car size={20} className="text-gray-500" />
          <span className="text-sm text-gray-600">控车操作</span>
        </div>
        <button
          onClick={() => {
            setShowTripInfo(true);
            setVehicleStatus('使用中');
          }}
          className="bg-primary text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-600 transition-colors"
        >
          立即用车
        </button>
      </div>
    </div>
  );

  const renderTripStatus = () => (
    <div className="bg-white rounded-xl p-6 mb-6 shadow-sm">
      <div className="pt-3 border-t">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">当前行程</h2>
        {/* <h4 className="text-sm font-medium text-gray-700 mb-3">当前消耗</h4> */}

        {/* 参考首页信息面板样式的消耗信息卡片 */}
        <div className="rounded-xl border bg-card text-card-foreground shadow">
          <div className="p-4">
            <div className="grid grid-cols-2 gap-4">
              {consumptionStats.map((stat, index) => {
                const IconComponent = stat.icon;
                return (
                  <div key={index} className="flex items-center gap-3">
                    <div className="p-2 rounded-lg text-white bg-red-500">
                      <IconComponent size={20} />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-gray-800">
                        {stat.value}
                      </p>
                      <p className="text-sm text-muted-foreground">{stat.label}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <div className="pt-3 border-t">
        {/* <h2 className="text-lg font-semibold text-gray-800 mb-4">当前行程</h2> */}

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-gray-600">起始时间</span>
            <span className="font-medium">{vehicleData.currentTrip.startTime}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-gray-600">起始里程</span>
            <span className="font-medium">{vehicleData.currentTrip.startMileage} km</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-gray-600">起始油量</span>
            <span className="font-medium">{vehicleData.currentTrip.startFuel}L</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-gray-600">起始电量</span>
            <span className="font-medium">{vehicleData.currentTrip.startBattery}KWH</span>
          </div>
        </div>
      </div>

      <div className="mt-4 p-3 bg-blue-50 rounded-lg">
        <p className="text-sm text-blue-800">
          本次用车不收取时长/里程费用，仅需补齐燃油与电量消耗。
        </p>
      </div>
    </div>
  );

  const renderEndTripModal = () => (
    showEndTrip && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">结束用车</h3>
            <button onClick={() => setShowEndTrip(false)} className="text-gray-400 hover:text-gray-600">
              <X size={20} />
            </button>
          </div>

          <div className="space-y-4">
            <p className="text-gray-600">请确认以下信息：</p>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span>起始油量：</span>
                <span>{vehicleData.currentTrip.startFuel}L</span>
              </div>
              <div className="flex justify-between">
                <span>当前油量：</span>
                <span>{vehicleData.currentTrip.currentFuel}L</span>
              </div>
              <div className="flex justify-between">
                <span>起始电量：</span>
                <span>{vehicleData.currentTrip.startBattery}KWH</span>
              </div>
              <div className="flex justify-between">
                <span>当前电量：</span>
                <span>{vehicleData.currentTrip.currentBattery}KWH</span>
              </div>
            </div>

            <button
              onClick={() => {
                setShowEndTrip(false);
                setShowPayment(true);
              }}
              className="w-full bg-primary text-white py-3 rounded-lg font-medium hover:bg-blue-600 transition-colors"
            >
              确认结束
            </button>
          </div>
        </div>
      </div>
    )
  );

  const renderPaymentModal = () => {
    // 计算实际消耗量
    const fuelConsumption = vehicleData.currentTrip.startFuel - vehicleData.currentTrip.currentFuel;
    const batteryConsumption = vehicleData.currentTrip.startBattery - vehicleData.currentTrip.currentBattery;

    // 计算费用（假设燃油9元/L，电量1元/KWH）
    const fuelCost = fuelConsumption * 9;
    const batteryCost = batteryConsumption * 1;
    const totalCost = fuelCost + batteryCost + 10;

    return (
      showPayment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">费用结算</h3>
              <button onClick={() => setShowPayment(false)} className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div className="border rounded-lg p-3">
                <div className="flex justify-between mb-2">
                  <span className="font-medium">燃油消耗</span>
                  <span className="text-red-600">¥{fuelCost.toFixed(1)}</span>
                </div>
                <div className="text-sm text-gray-500">
                  ¥9/L × {fuelConsumption.toFixed(1)}L
                </div>
              </div>

              <div className="border rounded-lg p-3">
                <div className="flex justify-between mb-2">
                  <span className="font-medium">电量消耗</span>
                  <span className="text-red-600">¥{batteryCost.toFixed(1)}</span>
                </div>
                <div className="text-sm text-gray-500">
                  ¥1/KWH × {batteryConsumption.toFixed(1)}KWH
                </div>
              </div>

              <div className="border rounded-lg p-3">
                <div className="flex justify-between mb-2">
                  <span className="font-medium">能源补充服务费</span>
                  <span className="text-red-600">¥10</span>
                </div>
                <div className="text-sm text-gray-500">
                  {/* ¥1/KWH × {batteryConsumption.toFixed(1)}KWH */}
                </div>
              </div>

              <div className="border-t pt-3">
                <div className="flex justify-between text-lg font-bold">
                  <span>合计</span>
                  <span className="text-red-600">¥{totalCost.toFixed(1)}</span>
                </div>
              </div>

              <button className="w-full bg-primary text-white py-3 rounded-lg font-medium hover:bg-blue-600 transition-colors"
                onClick={() => {
                  setShowTripInfo(false);
                  setShowPayment(false);
                  setVehicleStatus('空闲');
                }}
              >
                确认支付
              </button>
            </div>
          </div>
        </div>
      )
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-32">
      {/* 头部区域 */}
      {renderVehicleInfoCard()}

      {/* 控车操作区 */}
      {renderControlButtons()}

      {/* 行程状态区 */}
      {showTripInfo && renderTripStatus()}

      {/* 底部操作按钮 */}
      {showTripInfo && (
        <div className="fixed bottom-20 left-0 right-0 p-4 bg-white border-t">
          <div className="flex space-x-4">
            {/* <button className="flex-1 flex items-center justify-center space-x-2 bg-blue-100 text-blue-700 py-3 rounded-lg font-medium">
              <Navigation size={20} />
              <span>导航到车辆</span>
            </button> */}
            <button
              onClick={() => {
                // setShowEndTrip(true);
                setShowTripInfo(false);
                setShowPayment(true);
                setVehicleStatus('空闲');
              }}
              className="flex-1 flex items-center justify-center space-x-2 bg-red-600 text-white py-3 rounded-lg font-medium hover:bg-red-700 transition-colors"
            >
              <X size={20} />
              <span>结束用车</span>
            </button>
          </div>
        </div>
      )}

      {/* 模态框 */}
      {renderEndTripModal()}
      {renderPaymentModal()}
    </div>
  );
};

export default VehiclePage;
